import { useEffect, useRef, useState } from "react";
import { useAuthV2 } from "@cliently/auth-client";
import { configureApiAuth, setAuthToken, api } from "../lib/api";
import { useAuthStore } from "../stores/auth.store";

/**
 * Équivalent auth-v2 de `useAuthSync`. Configure l'intercepteur axios, charge
 * `/auth/me` et synchronise le store métier (`User`, `Organization`).
 *
 * Renvoie :
 *  - `false` : pas encore prêt (token en cours de récupération ou sign-out).
 *  - `true`  : token configuré et profil chargé.
 *  - `"client_blocked"` : tentative d'accès à l'app pro avec un compte client.
 */
export const useAuthSyncV2 = () => {
  const { isSignedIn, isLoaded, getToken, signOut } = useAuthV2();
  const orgLoaded = useRef(false);
  const [ready, setReady] = useState(false);
  const [clientBlocked, setClientBlocked] = useState(false);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      setAuthToken(null);
      orgLoaded.current = false;
      useAuthStore.getState().reset();
      setReady(false);
      setClientBlocked(false);
      return;
    }

    configureApiAuth(getToken);

    let cancelled = false;
    const sync = async () => {
      const token = await getToken();
      if (cancelled) return;
      setAuthToken(token);
      setReady(Boolean(token));

      if (token) {
        try {
          const { data } = await api.get("/auth/me");
          if (!cancelled) {
            if (data.features) {
              useAuthStore.getState().setFeatures(data.features);
            }
            if (!orgLoaded.current) {
              if (data.user) useAuthStore.getState().setUser(data.user);
              if (data.org) useAuthStore.getState().setOrg(data.org);
              orgLoaded.current = true;
            }
          }
        } catch (err: unknown) {
          if (!cancelled) {
            const e = err as {
              response?: { status?: number; data?: { message?: string } };
            };
            const status = e?.response?.status;
            const msg = e?.response?.data?.message ?? "";
            if ((status === 401 || status === 403) && msg.includes("client")) {
              setClientBlocked(true);
              setTimeout(() => void signOut(), 2500);
            }
          }
        }
      }
    };
    void sync();

    const interval = setInterval(() => void sync(), 50_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [getToken, isSignedIn, isLoaded, signOut]);

  if (clientBlocked) return "client_blocked" as const;
  return ready;
};
